import { Trash2, ShoppingBag, ArrowLeft, CreditCard, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../../hooks/cart/useCart";
import { PageContainer } from "../../../components/ui/PageContainer";

export default function CartPage() {
  const navigateTo = useNavigate();
  const { items, subtotal, deliveryFee, total, isLoading, updateQuantity, removeFromCart } =
    useCart();

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR').format(price) + ' FCFA';
  };

  const handleUpdateQuantity = (item: any, newQuantity: number) => {
    if (newQuantity < 1) return;
    updateQuantity(item.productId, newQuantity, item.variantId);
  };

  const handleRemoveItem = (item: any) => {
    console.log(item)
    removeFromCart(item.productId, item.variantId);
  };

  if (isLoading) {
    return (
      <PageContainer>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </PageContainer>
    );
  }

  if (items.length === 0) {
    return (
      <PageContainer>
        <div className="min-h-[60vh] flex flex-col items-center justify-center text-center py-16 px-4">
          <div className="w-20 h-20 md:w-24 md:h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4 md:mb-6">
            <ShoppingBag className="w-10 h-10 md:w-12 md:h-12 text-gray-400" />
          </div>
          <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-2">
            Votre panier est vide
          </h2>
          <p className="text-sm md:text-base text-gray-500 mb-4 md:mb-6">
            Découvrez nos produits et trouvez ce qui vous plaît
          </p>
          <button
            onClick={() => navigateTo("/products")}
            className="inline-flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors text-sm md:text-base"
          >
            <ShoppingBag className="w-4 h-4" />
            Découvrir nos produits
          </button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="py-4 md:py-6 lg:py-8 px-3 md:px-0">
        {/* Header */}
        <div className="flex items-center gap-2 md:gap-4 mb-4 md:mb-6">
          <button
            onClick={() => navigateTo("/products")}
            className="p-1.5 md:p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4 md:w-5 md:h-5" />
          </button>
          <h1 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900">
            Mon panier ({items.length} article{items.length > 1 ? 's' : ''})
          </h1>
        </div>

        <div className="flex flex-col lg:grid lg:grid-cols-3 gap-6 md:gap-8">
          {/* Liste des produits */}
          <div className="lg:col-span-2 space-y-3 md:space-y-4">
            {items.map((item, index) => (
              <div
                key={item.id + index}
                className="flex gap-3 md:gap-4 p-3 md:p-4 bg-white border border-gray-100 rounded-xl shadow-sm"
              >
                {/* Image */}
                <div className="relative w-20 h-20 md:w-24 md:h-24 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Infos */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm md:text-base font-semibold text-gray-800 mb-1 line-clamp-2">
                    {item.name}
                  </h3>
                  {item.variantValue && (
                    <p className="text-xs text-gray-500 mb-1">
                      Variante: {item.variantValue}
                    </p>
                  )}
                  <p className="text-primary font-bold text-sm md:text-base mb-2">
                    {item.priceLabel}
                  </p>

                  {/* Quantité */}
                  <div className="flex items-center justify-between md:justify-start gap-2">
                    <div className="flex items-center gap-1 md:gap-2">
                      <button
                        onClick={() => handleUpdateQuantity(item, item.quantity - 1)}
                        className="w-7 h-7 md:w-8 md:h-8 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors flex items-center justify-center"
                      >
                        -
                      </button>
                      <span className="w-8 md:w-10 text-center font-medium text-sm md:text-base">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => handleUpdateQuantity(item, item.quantity + 1)}
                        className="w-7 h-7 md:w-8 md:h-8 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors flex items-center justify-center"
                      >
                        +
                      </button>
                    </div>
                    <button
                      onClick={() => handleRemoveItem(item)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4 md:w-5 md:h-5" />
                    </button>
                  </div>
                </div>

                {/* Prix total ligne - Desktop */}
                <div className="hidden md:block text-right">
                  <p className="font-semibold text-gray-900">
                    {formatPrice(item.price * item.quantity)}
                  </p>
                  {item.quantity > 1 && (
                    <p className="text-xs text-gray-400 mt-1">
                      {formatPrice(item.price)}/unité
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Résumé commande */}
          <div className="lg:col-span-1 order-first lg:order-last mb-4 lg:mb-0">
            <div className="bg-gray-50 rounded-xl p-4 md:p-6 sticky top-[140px]">
              <h2 className="text-base md:text-lg font-bold text-gray-900 mb-3 md:mb-4">
                Résumé de la commande
              </h2>

              <div className="space-y-2 md:space-y-3 mb-3 md:mb-4 pb-3 md:pb-4 border-b border-gray-200">
                <div className="flex justify-between text-gray-600 text-sm md:text-base">
                  <span>Sous-total</span>
                  <span>{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between text-gray-600 text-sm md:text-base">
                  <span>Livraison</span>
                  <span>{formatPrice(deliveryFee)}</span>
                </div>
              </div>

              <div className="flex justify-between mb-4 md:mb-6">
                <span className="font-bold text-gray-900 text-sm md:text-base">Total</span>
                <span className="text-lg md:text-xl font-bold text-primary">
                  {formatPrice(total)}
                </span>
              </div>

              <button
                onClick={() => navigateTo("/checkout")}
                className="w-full bg-primary text-white py-2.5 md:py-3 rounded-xl font-semibold hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 text-sm md:text-base"
              >
                <CreditCard className="w-4 h-4" />
                Valider la commande
              </button>

              <p className="text-xs text-gray-400 text-center mt-3 md:mt-4">
                Livraison à Douala, Yaoundé et dans toutes les grandes villes
              </p>
            </div>
          </div>
        </div>

        {/* Prix total ligne - Mobile */}
        <div className="mt-4 pt-4 border-t border-gray-200 md:hidden">
          {items.map((item, index) => (
            <div key={item.id + index} className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">{item.name.substring(0, 30)}...</span>
              <span className="font-semibold">{formatPrice(item.price * item.quantity)}</span>
            </div>
          ))}
        </div>
      </div>
    </PageContainer>
  );
}